import React, { useState } from "react";
import useSWR from "swr";
import { api, formatDKK } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { MapPin, CheckCircle2, XCircle } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

function parsePostcode(pc) {
  const d = (pc || "").replace(/\D/g, "").slice(0, 4);
  return d.length === 4 ? parseInt(d, 10) : null;
}

export default function PostcodeChecker({ compact = false }) {
  const { t } = useLang();
  const { data } = useSWR("/pricing/public", fetcher, { revalidateOnFocus: false });
  const [pc, setPc] = useState("");
  const [result, setResult] = useState(null);

  const check = () => {
    const code = parsePostcode(pc);
    if (!code) { setResult({ invalid: true }); return; }
    const zone = (data?.travel_zones || []).find((z) => code >= z.from && code <= z.to);
    if (!zone || !zone.supported) { setResult({ supported: false }); return; }
    setResult({ supported: true, zone });
  };

  return (
    <div className={compact ? "" : "bg-white border border-stone-200 rounded-2xl p-6 sm:p-8"} data-testid="postcode-checker">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MapPin className="w-5 h-5 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={pc}
            onChange={(e) => setPc(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && check()}
            inputMode="numeric"
            placeholder={t.area.placeholder}
            className="w-full pl-10 pr-4 py-3.5 rounded-xl border border-stone-300 focus:border-[#1E3A2F] focus:ring-1 focus:ring-[#1E3A2F] outline-none text-base"
            data-testid="postcode-checker-input"
          />
        </div>
        <button
          onClick={check}
          className="px-6 py-3.5 rounded-xl bg-[#1E3A2F] text-white font-semibold hover:bg-[#162D24] transition-colors"
          data-testid="postcode-checker-btn"
        >
          {t.area.check}
        </button>
      </div>

      {result && (
        <div className="mt-4" data-testid="postcode-checker-result">
          {result.invalid && <p className="text-sm text-red-600">{t.area.invalid}</p>}
          {result.supported === false && (
            <div className="flex items-start gap-2 text-stone-700 bg-stone-50 rounded-xl p-4">
              <XCircle className="w-5 h-5 text-stone-400 mt-0.5 shrink-0" />
              <p className="text-sm">{t.area.notSupported}</p>
            </div>
          )}
          {result.supported === true && (
            <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-emerald-900">{t.area.supported} — {result.zone.name}</p>
                <p className="text-emerald-800/80 mt-0.5">
                  {t.area.supportedFee}{" "}
                  {result.zone.surcharge > 0 ? formatDKK(result.zone.surcharge) : t.area.free}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
